import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Box, Typography, CircularProgress, useTheme } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import RefreshIcon from "@mui/icons-material/Refresh";
import { PieChart, Pie, Cell as PieSliceCell, ResponsiveContainer } from "recharts";
import { getAuthToken } from "../../utils/getAuthToken";
import CallsDateRangePicker from "./CallsDateRangePicker";
import { PALETTE, STATUS_COLORS, STATUS_LABELS, formatDuration, formatHoursMinutes, formatCount } from "./callsPalette";

const SERVER_DOMAIN = process.env.REACT_APP_CALLING_URL || process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";
const SEARCH_DEBOUNCE_MS = 300;

function toDateInputValue(date) {
  return date.toISOString().slice(0, 10);
}

function defaultRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 6);
  return { start: toDateInputValue(start), end: toDateInputValue(end) };
}

async function fetchAgentStats(startDate, endDate) {
  const token = await getAuthToken();
  const res = await axios.get(`${SERVER_DOMAIN}/api/calling/agent-stats`, {
    headers: { Authorization: `Bearer ${token}` },
    params: { startDate, endDate },
  });
  return res.data;
}

function ErrorState({ onRetry }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, py: 8 }}>
      <ErrorOutlineIcon sx={{ fontSize: 32, color: PALETTE.critical.ring }} />
      <Typography sx={{ fontWeight: 700 }}>Couldn't load this report</Typography>
      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>Check your connection and try again.</Typography>
      <Box
        component="button"
        onClick={onRetry}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          mt: 1,
          px: 2,
          py: 1,
          borderRadius: 2,
          border: "none",
          bgcolor: PALETTE.blue.fill,
          color: "#fff",
          fontWeight: 700,
          fontSize: 13,
          cursor: "pointer",
          fontFamily: "inherit",
        }}
      >
        <RefreshIcon sx={{ fontSize: 16 }} />
        Retry
      </Box>
    </Box>
  );
}

// Business-wide call-time cap this cycle — omitted entirely when no cap is
// configured (limitSeconds <= 0), same reasoning as Overview's meter: an
// "unlimited plan" line with nothing to compare against is noise, not signal.
function UsageSummaryLine({ usedSeconds, limitSeconds }) {
  if (!limitSeconds || limitSeconds <= 0) return null;
  const pct = Math.min(100, Math.round((usedSeconds / limitSeconds) * 100));
  const color = usedSeconds > limitSeconds ? PALETTE.critical.ring : pct >= 90 ? PALETTE.warning.ring : "text.secondary";
  return (
    <Typography sx={{ fontSize: 13.5, color, fontWeight: 600, mb: 2 }}>
      Business call time this cycle: {formatHoursMinutes(usedSeconds)} of {formatHoursMinutes(limitSeconds)} ({pct}%)
    </Typography>
  );
}

/** Bigger donut with a center hero number — Recharts, not a MUI gauge, sized
 * to actually read at a glance instead of the cramped 220px version this
 * page shipped with before. No floating Tooltip: it collided with the
 * center label on hover, so the center content itself swaps to the hovered
 * slice's name/count instead — a single source of truth for "what am I
 * looking at" instead of two overlapping ones. */
function OutcomeDonut({ data, totalCalls }) {
  const theme = useTheme();
  const [activeIndex, setActiveIndex] = useState(null);
  const chartData = useMemo(
    () =>
      data
        .filter((d) => d.count > 0)
        .map((d) => ({ name: STATUS_LABELS[d.status] || d.status, value: d.count, status: d.status, color: STATUS_COLORS[d.status] || theme.palette.text.secondary }))
        .sort((a, b) => b.value - a.value),
    [data, theme]
  );

  if (chartData.length === 0) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: 320, color: "text.secondary" }}>
        No calls in this range.
      </Box>
    );
  }

  const active = activeIndex != null ? chartData[activeIndex] : null;

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
      <Box sx={{ position: "relative", width: 300, height: 300, flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={chartData}
              dataKey="value"
              nameKey="name"
              innerRadius={92}
              outerRadius={144}
              paddingAngle={3}
              startAngle={90}
              endAngle={450}
              onMouseEnter={(_, i) => setActiveIndex(i)}
              onMouseLeave={() => setActiveIndex(null)}
            >
              {chartData.map((entry, i) => (
                <PieSliceCell
                  key={i}
                  fill={entry.color}
                  stroke={theme.palette.background.paper}
                  strokeWidth={2}
                  style={{
                    cursor: "pointer",
                    opacity: active && active.status !== entry.status ? 0.35 : 1,
                    transition: "opacity 0.15s ease",
                  }}
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <Box sx={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
          {active ? (
            <>
              <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: active.color, mb: 0.75 }} />
              <Typography sx={{ fontSize: 32, fontWeight: 700, lineHeight: 1 }}>{formatCount(active.value)}</Typography>
              <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: "text.secondary", mt: 0.5, textAlign: "center", maxWidth: 140 }}>
                {active.name}
              </Typography>
            </>
          ) : (
            <>
              <Typography sx={{ fontSize: 36, fontWeight: 700, lineHeight: 1 }}>{formatCount(totalCalls)}</Typography>
              <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", color: "text.secondary", mt: 0.5 }}>
                Total calls
              </Typography>
            </>
          )}
        </Box>
      </Box>

      {/* Custom legend — a colored dot + label + count + share, capped to a
          tight column width so rows read as one compact block instead of
          stretching label/count apart across the card's full width. */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, width: 220, flexShrink: 0 }}>
        {chartData.map((entry, i) => (
          <Box
            key={entry.status}
            onMouseEnter={() => setActiveIndex(i)}
            onMouseLeave={() => setActiveIndex(null)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              py: 0.75,
              px: 1,
              borderRadius: 1.5,
              cursor: "pointer",
              bgcolor: activeIndex === i ? theme.palette.action.hover : "transparent",
            }}
          >
            <Box sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: entry.color, flexShrink: 0 }} />
            <Typography sx={{ fontSize: 13.5, fontWeight: 600, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {entry.name}
            </Typography>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{formatCount(entry.value)}</Typography>
            <Typography sx={{ fontSize: 12, color: "text.secondary", minWidth: 36, textAlign: "right" }}>
              {totalCalls > 0 ? Math.round((entry.value / totalCalls) * 1000) / 10 : 0}%
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

const COLUMNS = [
  { key: "agentName", label: "Agent", align: "left", sortable: true },
  { key: "answered", label: "Answered", align: "right", sortable: true },
  { key: "noAnswer", label: "No Answer", align: "right", sortable: true },
  { key: "abandoned", label: "Abandoned", align: "right", sortable: true },
  { key: "declined", label: "Declined", align: "right", sortable: true },
  { key: "failed", label: "Failed", align: "right", sortable: true },
  { key: "total", label: "Total", align: "right", sortable: true },
  { key: "totalRingSeconds", label: "Ring Time", align: "right", sortable: true },
  { key: "totalTalkSeconds", label: "Talk Time", align: "right", sortable: true },
];

/** Custom table — plain Box rows (same primitive CallLogsTable.jsx already
 * uses elsewhere in this app), not MUI's Table/TableCell, with generous
 * row height, sortable headers, and a sticky header for a long agent list. */
function AgentTable({ rows, search, onSearchChange, sort, onSortChange }) {
  const theme = useTheme();

  const sortedRows = useMemo(() => {
    const copy = [...rows];
    copy.sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      const cmp = typeof av === "string" ? av.localeCompare(bv) : av - bv;
      return sort.dir === "asc" ? cmp : -cmp;
    });
    return copy;
  }, [rows, sort]);

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 2.5, py: 2 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 16, flex: 1 }}>Agent performance</Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            width: 240,
            height: 38,
            px: 1.5,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)",
          }}
        >
          <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          <Box
            component="input"
            placeholder="Search agent…"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            sx={{
              border: "none",
              outline: "none",
              bgcolor: "transparent",
              fontSize: 13.5,
              fontFamily: "inherit",
              color: "text.primary",
              width: "100%",
              "&::placeholder": { color: "text.secondary" },
            }}
          />
        </Box>
      </Box>

      {sortedRows.length === 0 ? (
        <Box sx={{ textAlign: "center", py: 8, color: "text.secondary", fontSize: 14 }}>
          {rows.length === 0 ? "No agent activity in this range." : "No agents match your search."}
        </Box>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Box sx={{ minWidth: 780 }}>
            {/* Header */}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1.6fr repeat(8, 1fr)",
                borderTop: `1px solid ${theme.palette.divider}`,
                borderBottom: `1px solid ${theme.palette.divider}`,
                bgcolor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.015)",
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}
            >
              {COLUMNS.map((col) => {
                const active = sort.key === col.key;
                return (
                  <Box
                    key={col.key}
                    onClick={() => onSortChange(col.key)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: col.align === "right" ? "flex-end" : "flex-start",
                      gap: 0.5,
                      px: 2,
                      py: 1.5,
                      cursor: "pointer",
                      userSelect: "none",
                      "&:hover": { color: "text.primary" },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: 0.4,
                        textTransform: "uppercase",
                        color: active ? "text.primary" : "text.secondary",
                      }}
                    >
                      {col.label}
                    </Typography>
                    {active ? (
                      sort.dir === "asc" ? (
                        <ArrowUpwardIcon sx={{ fontSize: 13 }} />
                      ) : (
                        <ArrowDownwardIcon sx={{ fontSize: 13 }} />
                      )
                    ) : (
                      <UnfoldMoreIcon sx={{ fontSize: 13, opacity: 0.4 }} />
                    )}
                  </Box>
                );
              })}
            </Box>

            {/* Rows */}
            {sortedRows.map((row, i) => (
              <motion.div
                key={row.agentId}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.2, delay: Math.min(i * 0.02, 0.25) }}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.6fr repeat(8, 1fr)",
                  borderBottom: `1px solid ${theme.palette.divider}`,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", px: 2, py: 1.75, fontSize: 14, fontWeight: 700 }}>{row.agentName}</Box>
                <Cell value={formatCount(row.answered)} color={STATUS_COLORS.completed} />
                <Cell value={formatCount(row.noAnswer)} color={STATUS_COLORS.missed} />
                <Cell value={formatCount(row.abandoned)} color={STATUS_COLORS.abandoned} />
                <Cell value={formatCount(row.declined)} color={STATUS_COLORS.declined} />
                <Cell value={formatCount(row.failed)} color={STATUS_COLORS.failed} />
                <Cell value={formatCount(row.total)} bold />
                <Cell value={formatDuration(row.totalRingSeconds)} />
                <Cell value={formatDuration(row.totalTalkSeconds)} />
              </motion.div>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

function Cell({ value, color, bold }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", px: 2, py: 1.75 }}>
      {color && <Box sx={{ width: 7, height: 7, borderRadius: "50%", bgcolor: color, mr: 1 }} />}
      <Typography sx={{ fontSize: 14, fontWeight: bold ? 700 : 500 }}>{value}</Typography>
    </Box>
  );
}

export default function CallsAgentReport() {
  const theme = useTheme();
  const [range, setRange] = useState(defaultRange);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState({ key: "total", dir: "desc" });
  const searchDebounceRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const result = await fetchAgentStats(range.start, range.end);
      setData(result);
    } catch (err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [range]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => setSearch(value.trim().toLowerCase()), SEARCH_DEBOUNCE_MS);
  };

  const handleSortChange = (key) => {
    setSort((prev) => (prev.key === key ? { key, dir: prev.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" }));
  };

  const filteredRows = useMemo(() => {
    const rows = data?.byAgent || [];
    if (!search) return rows;
    return rows.filter((r) => r.agentName.toLowerCase().includes(search));
  }, [data, search]);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Reports
        </Typography>
        <CallsDateRangePicker value={range} onChange={setRange} />
      </Box>

      {loading && !data ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={24} />
        </Box>
      ) : error && !data ? (
        <ErrorState onRetry={load} />
      ) : (
        <>
          <UsageSummaryLine usedSeconds={data?.callUsage?.usedSeconds ?? 0} limitSeconds={data?.callUsage?.limitSeconds ?? 0} />

          <Box
            sx={{
              borderRadius: 2.5,
              p: 3,
              mb: 3,
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: 16, mb: 2 }}>Call outcomes</Typography>
            <OutcomeDonut data={data?.statusBreakdown || []} totalCalls={(data?.statusBreakdown || []).reduce((sum, s) => sum + s.count, 0)} />
          </Box>

          <Box
            sx={{
              borderRadius: 2.5,
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.divider}`,
              overflow: "hidden",
            }}
          >
            <AgentTable rows={filteredRows} search={searchInput} onSearchChange={handleSearchChange} sort={sort} onSortChange={handleSortChange} />
          </Box>
        </>
      )}
    </Box>
  );
}
